import { Building2 } from "lucide-react";
import { PROPOSAL_SEARCH_PARAM } from "@/features/enterprise-proposals-grid/_logic/constants";
import type { ProposalCard } from "@/features/enterprise-proposals-grid/_types/ui-types";
import type { PaletteItem } from "./types";

/** Prefixes every proposal row's id so a venue can never collide with a registry item. */
export const PROPOSAL_ID_PREFIX = "proposal:";

/**
 * Below this the query is not a venue name yet, it is the first letter of one.
 * Fetching on it costs a round trip per keystroke and returns most of the event.
 */
export const MIN_PROPOSAL_QUERY_LENGTH = 2;

/** Enough to find the hotel you meant without pushing the rest of the palette off screen. */
export const MAX_PROPOSAL_ROWS = 6;

/** One row per venue, whatever the shape of the cards it was derived from. */
export interface ProposalVenue {
  venueStaticId: number;
  venueName: string;
  location: string | null;
}

/**
 * Collapses proposal cards to one entry per venue.
 *
 * `getProposals` returns one card per *offer*, so a hotel holding three quotes
 * comes back three times. Three identical rows in a palette read as a bug, and
 * they all go to the same place.
 *
 * @param cards - Proposal cards for one event, already narrowed by the search.
 * @returns One venue per `venueStaticId`, in the order they first appeared.
 */
export function toProposalVenues(cards: ProposalCard[]): ProposalVenue[] {
  const byVenue = new Map<number, ProposalVenue>();
  cards.forEach((card) => {
    if (byVenue.has(card.venueStaticId)) return;
    byVenue.set(card.venueStaticId, {
      venueStaticId: card.venueStaticId,
      venueName: card.venueName,
      location: [card.city, card.state ?? card.country].filter(Boolean).join(", ") || null,
    });
  });
  return [...byVenue.values()];
}

/**
 * Where a proposal row goes.
 *
 * There is no route for a single proposal: the grid opens each one in a modal,
 * so the deepest link that exists is the grid narrowed to that venue. Seeding
 * the grid's own search box is the honest version of "take me to it", and it
 * survives the venue having several offers, which a link to one offer would not.
 *
 * Carries the registry's `{eventId}` placeholder rather than a resolved id, so
 * these rows resolve through the same path as every other event-scoped item.
 *
 * @param venueName - The venue to narrow the grid to.
 * @returns The href, with `{eventId}` still in it.
 */
export function buildProposalHref(venueName: string): string {
  return `/dashboard/source/{eventId}/proposals?${PROPOSAL_SEARCH_PARAM}=${encodeURIComponent(venueName)}`;
}

/**
 * Turns venues into palette rows.
 *
 * Scoped to `event` like every other row that needs one, so selecting a stale
 * row after the scope was cleared asks which event rather than navigating to
 * `/dashboard/source/{eventId}/proposals` literally.
 *
 * @param venues - Deduped venues from {@link toProposalVenues}.
 * @returns At most {@link MAX_PROPOSAL_ROWS} palette items.
 */
export function buildProposalItems(venues: ProposalVenue[]): PaletteItem[] {
  return venues.slice(0, MAX_PROPOSAL_ROWS).map((venue) => ({
    id: `${PROPOSAL_ID_PREFIX}${venue.venueStaticId}`,
    label: venue.venueName,
    group: "proposals" as const,
    icon: Building2,
    scope: "event" as const,
    hint: venue.location ?? undefined,
    run: { kind: "navigate" as const, href: buildProposalHref(venue.venueName) },
  }));
}
